import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Plus, Upload, Users, FileArrowUp, Eye, Pencil, User, EnvelopeSimple, Phone, MapPin, Briefcase, GraduationCap, CurrencyGbp, Clock, Link, Notepad, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import CallButton from "@/components/CallButton";
import Pagination from "@/components/Pagination";
import CreatorSelect from "@/components/CreatorSelect";

const EMPTY_FORM = { created_by: "",
  first_name: "", last_name: "", email: "", phone: "", skills: "",
  experience_level: "mid", location: "", current_role: "",
  desired_salary: "", availability: "", linkedin_url: "", education: "", notes: "",
};

const EXP_LABELS = { junior: "Junior", mid: "Mid-Level", senior: "Senior", lead: "Lead", executive: "Executive" };

export default function CandidatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const cvInputRef = useRef(null);
  const batchInputRef = useRef(null);
  const [showExtSearch, setShowExtSearch] = useState(false);
  const [extResults, setExtResults] = useState([]);
  const [extQuery, setExtQuery] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [cvFileRef, setCvFileRef] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCandidates = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates?search=${search}&page=${page}&limit=25`);
      setCandidates(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const openAddForm = () => { setEditingCandidate(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };

  const openEditForm = (c) => {
    setEditingCandidate(c);
    setForm({
      first_name: c.first_name || "", last_name: c.last_name || "",
      email: c.email || "", phone: c.phone || "",
      skills: Array.isArray(c.skills) ? c.skills.join(", ") : (c.skills || ""),
      experience_level: c.experience_level || "mid", location: c.location || "",
      current_role: c.current_role || "", desired_salary: c.desired_salary || "",
      availability: c.availability || "", linkedin_url: c.linkedin_url || "",
      education: c.education || "", notes: c.notes || "",
    });
    setShowDetail(false);
    setShowForm(true);
  };

  const openDetail = async (c) => {
    try { const { data } = await api.get(`/candidates/${c.id}`); setSelectedCandidate(data); }
    catch { setSelectedCandidate(c); }
    setShowDetail(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, skills: typeof form.skills === "string" ? form.skills.split(",").map((s) => s.trim()).filter(Boolean) : form.skills };
    if (cvFileRef && !editingCandidate) {
      payload.cv_file = cvFileRef;
    }
    try {
      if (editingCandidate) {
        await api.put(`/candidates/${editingCandidate.id}`, payload);
        toast.success("Candidate updated");
      } else {
        await api.post("/candidates", payload);
        toast.success("Candidate added");
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setCvFileRef(null);
      setEditingCandidate(null);
      loadCandidates();
    } catch { toast.error("Failed to save candidate"); }
    finally { setSaving(false); }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = "";
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/candidates/upload-cv", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.parsed_data) {
        setForm({
          ...EMPTY_FORM,
          first_name: data.parsed_data.first_name || "",
          last_name: data.parsed_data.last_name || "",
          email: data.parsed_data.email || "",
          phone: data.parsed_data.phone || "",
          skills: (data.parsed_data.skills || []).join(", "),
          experience_level: data.parsed_data.experience_level || "mid",
          notes: data.cv_file?.id ? `CV: ${data.filename} (file: ${data.cv_file.id})` : "",
        });
        if (data.cv_file?.id) {
          setCvFileRef(data.cv_file);
        }
        setShowUpload(false);
        setEditingCandidate(null);
        setShowForm(true);
        toast.success(`CV parsed — ${data.filename}. Review and save.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse CV");
    }
  };

  const handleBatchImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/candidates/batch-import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} candidates imported`);
      loadCandidates();
    } catch { toast.error("Failed to import"); }
  };

  const handleExtSearch = async () => {
    try {
      const { data } = await api.get(`/external/apollo/candidate-search?q=${extQuery}`);
      setExtResults(data.items || []);
    } catch { toast.error("Search failed"); }
  };

  const importCandidate = async (c) => {
    try { await api.post("/candidates", c); toast.success(`${c.first_name} imported`); loadCandidates(); }
    catch { toast.error("Import failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete candidate?")) return;
    try { await api.delete(`/candidates/${id}`); toast.success("Deleted"); setShowDetail(false); loadCandidates(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const getScoreColor = (s) => s >= 70 ? "bg-green-100 text-green-800" : s >= 40 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800";
  const f = (v) => v || "-";

  return (
    <div data-testid="candidates-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Candidates</h1>
          <p className="text-sm text-[#6B7280]">{total} candidates sourced</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowExtSearch(true)} className="gap-2 border-[#E5E7EB] text-sm" data-testid="ext-candidate-search-btn">
            <MagnifyingGlass size={14} /> Apollo Search
          </Button>
          <Button variant="outline" onClick={() => setShowUpload(true)} className="gap-2 border-[#E5E7EB] text-sm" data-testid="upload-cv-btn">
            <Upload size={14} /> Upload CV
          </Button>
          <Button
            type="button"
            variant="outline"
            className="gap-2 border-[#E5E7EB] text-sm"
            onClick={() => batchInputRef.current && batchInputRef.current.click()}
          >
            <FileArrowUp size={14} /> Batch Import
          </Button>
          <input ref={batchInputRef} type="file" accept=".csv" onChange={handleBatchImport} className="hidden" data-testid="batch-import-input" />
          <Button onClick={openAddForm} className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-sm" data-testid="add-candidate-btn">
            <Plus size={14} /> Add Candidate
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input placeholder="Search candidates..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 border-[#E5E7EB]" data-testid="candidate-search-input" />
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bm-table-header">
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Role</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Skills</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Location</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Created By</TableHead>
                <TableHead className="text-xs">Score</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => <TableRow key={i}><TableCell colSpan={6}><div className="h-8 skeleton-shimmer rounded" /></TableCell></TableRow>)
              ) : candidates.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#6B7280]">No candidates found. Start sourcing!</TableCell></TableRow>
              ) : candidates.map((c) => (
                <TableRow key={c.id} className="hover:bg-[#F9FAFB] transition-colors cursor-pointer" onClick={() => openDetail(c)} data-testid={`candidate-row-${c.id}`}>
                  <TableCell className="font-medium text-[#0F172A]">{c.first_name} {c.last_name}</TableCell>
                  <TableCell className="hidden md:table-cell text-[#6B7280] text-sm">{c.current_role || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex gap-1 flex-wrap">
                      {(c.skills || []).slice(0, 3).map((s, i) => <Badge key={i} variant="secondary" className="text-[10px] bg-[#F3F4F6] text-[#374151]">{s}</Badge>)}
                      {(c.skills || []).length > 3 && <Badge variant="secondary" className="text-[10px]">+{c.skills.length - 3}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-[#6B7280] text-sm">{c.location || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-[#9CA3AF] text-xs">{c.created_by_name || "-"}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded text-xs font-medium ${getScoreColor(c.score)}`}>{c.score}</span></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openDetail(c)} className="text-[#0F172A] hover:bg-[#F3F4F6] text-xs" data-testid={`view-candidate-${c.id}`}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />
        </div>
      </div>

      {/* ==================== CANDIDATE DETAIL DIALOG ==================== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div>
              <DialogTitle className="text-[#0F172A] text-lg">{selectedCandidate?.first_name} {selectedCandidate?.last_name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                {selectedCandidate?.experience_level && <Badge variant="outline" className="text-[10px] border-[#E5E7EB]">{EXP_LABELS[selectedCandidate.experience_level] || selectedCandidate.experience_level}</Badge>}
                {selectedCandidate?.score != null && <span className={`px-2 py-0.5 rounded text-xs font-bold ${getScoreColor(selectedCandidate.score)}`}>Score: {selectedCandidate.score}</span>}
              </div>
            </div>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField icon={Briefcase} label="Current Role" value={f(selectedCandidate.current_role)} />
                <DetailField icon={MapPin} label="Location" value={f(selectedCandidate.location)} />
                <DetailField icon={EnvelopeSimple} label="Email" value={f(selectedCandidate.email)} />
                {selectedCandidate.phone ? (
                  <div className="flex items-start gap-2 p-2.5 bg-[#F9FAFB] rounded-lg">
                    <div className="w-7 h-7 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
                      <Phone size={14} weight="duotone" className="text-[#6B7280]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide font-medium">Phone</p>
                      <CallButton phone={selectedCandidate.phone} compact showNumber />
                    </div>
                  </div>
                ) : (
                  <DetailField icon={Phone} label="Phone" value="-" />
                )}
                <DetailField icon={CurrencyGbp} label="Desired Salary" value={f(selectedCandidate.desired_salary)} />
                <DetailField icon={Clock} label="Availability" value={f(selectedCandidate.availability)} />
                <DetailField icon={GraduationCap} label="Education" value={f(selectedCandidate.education)} />
                <DetailField icon={Link} label="LinkedIn" value={f(selectedCandidate.linkedin_url)} />
                <DetailField icon={Star} label="Source" value={f(selectedCandidate.source)} />
              </div>
              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCandidate.skills.map((s, i) => <Badge key={i} variant="secondary" className="text-xs bg-[#F3F4F6] text-[#374151]">{s}</Badge>)}
                  </div>
                </div>
              )}
              {selectedCandidate.notes && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Recruiter Notes</p>
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-[#374151] whitespace-pre-wrap">{selectedCandidate.notes}</div>
                </div>
              )}
              {selectedCandidate.cv_file?.id && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">CV Document</p>
                  <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-lg px-3 py-2 border border-[#E5E7EB]">
                    <Upload size={16} className="text-[#C5A059]" />
                    <span className="text-xs font-medium text-[#0F172A] flex-1 truncate">{selectedCandidate.cv_file.original_filename || "CV"}</span>
                    <Button variant="outline" size="sm" className="text-xs h-7" data-testid="download-cv-btn"
                      onClick={async () => {
                        try {
                          const resp = await api.get(`/files/download/${selectedCandidate.cv_file.id}`, { responseType: "blob" });
                          const url = window.URL.createObjectURL(new Blob([resp.data]));
                          const a = document.createElement("a"); a.href = url; a.download = selectedCandidate.cv_file.original_filename || "cv";
                          document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        } catch { toast.error("Download failed"); }
                      }}>Download</Button>
                  </div>
                </div>
              )}
              <div className="text-xs text-[#9CA3AF]">Created: {selectedCandidate.created_at ? new Date(selectedCandidate.created_at).toLocaleString() : "-"}</div>
              <Separator />
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => openEditForm(selectedCandidate)} className="gap-2 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="edit-candidate-btn">
                  <Pencil size={14} /> Edit Candidate
                </Button>
                {isAdmin && (
                  <Button variant="outline" onClick={() => handleDelete(selectedCandidate.id)} className="gap-1 border-[#EF4444] text-[#EF4444] hover:bg-red-50 ml-auto" data-testid="detail-delete-candidate-btn">Delete</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== ADD / EDIT DIALOG ==================== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#0F172A]">{editingCandidate ? "Edit Candidate" : "Add Candidate"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wide mb-2">Personal Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required className="h-10 border-[#E5E7EB]" data-testid="candidate-first-name" /></div>
                <div><Label className="text-xs">Last Name *</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required className="h-10 border-[#E5E7EB]" data-testid="candidate-last-name" /></div>
                <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 border-[#E5E7EB]" data-testid="candidate-email" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-10 border-[#E5E7EB]" data-testid="candidate-phone" /></div>
                <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. London" className="h-10 border-[#E5E7EB]" data-testid="candidate-location" /></div>
                <div><Label className="text-xs">LinkedIn URL</Label><Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/..." className="h-10 border-[#E5E7EB]" data-testid="candidate-linkedin" /></div>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wide mb-2">Professional</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Current Role</Label><Input value={form.current_role} onChange={(e) => setForm({ ...form, current_role: e.target.value })} className="h-10 border-[#E5E7EB]" data-testid="candidate-current-role" /></div>
                <div>
                  <Label className="text-xs">Experience Level</Label>
                  <Select value={form.experience_level} onValueChange={(v) => setForm({ ...form, experience_level: v })}>
                    <SelectTrigger className="h-10 border-[#E5E7EB]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="junior">Junior</SelectItem>
                      <SelectItem value="mid">Mid-Level</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2"><Label className="text-xs">Skills (comma-separated)</Label><Input value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="python, react, aws" className="h-10 border-[#E5E7EB]" data-testid="candidate-skills" /></div>
                <div><Label className="text-xs">Education</Label><Input value={form.education} onChange={(e) => setForm({ ...form, education: e.target.value })} placeholder="e.g. BSc Computer Science" className="h-10 border-[#E5E7EB]" data-testid="candidate-education" /></div>
                <div><Label className="text-xs">Desired Salary</Label><Input value={form.desired_salary} onChange={(e) => setForm({ ...form, desired_salary: e.target.value })} placeholder="e.g. 60,000 - 75,000" className="h-10 border-[#E5E7EB]" data-testid="candidate-salary" /></div>
                <div><Label className="text-xs">Availability</Label><Input value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="e.g. 2 weeks notice" className="h-10 border-[#E5E7EB]" data-testid="candidate-availability" /></div>
              </div>
            </div>
            <Separator />
            <div>
              <Label className="text-xs">Recruiter Notes (internal)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Internal notes about this candidate..." className="border-[#E5E7EB] bg-yellow-50" data-testid="candidate-notes-input" />
            </div>
            {!editingCandidate && (
              <CreatorSelect value={form.created_by} onChange={(v) => setForm({ ...form, created_by: v })} testId="candidate-creator-select" />
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0F172A]" data-testid="submit-candidate-btn">{saving ? "Saving..." : editingCandidate ? "Update Candidate" : "Add Candidate"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CV Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={(open) => { if (!open && cvInputRef.current) cvInputRef.current.value = ""; setShowUpload(open); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#0F172A] flex items-center gap-2"><Upload size={20} weight="duotone" className="text-[#C5A059]" /> Upload CV</DialogTitle></DialogHeader>
          <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-8 text-center">
            <Upload size={40} className="mx-auto text-[#6B7280] mb-3" />
            <p className="text-sm text-[#6B7280] mb-3">Upload a CV to auto-extract candidate data</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => cvInputRef.current && cvInputRef.current.click()}
            >
              Choose File
            </Button>
            <input ref={cvInputRef} type="file" accept=".txt,.csv,.doc,.docx,.pdf" onChange={handleCVUpload} className="hidden" data-testid="cv-file-input" />
          </div>
        </DialogContent>
      </Dialog>

      {/* External Search Dialog */}
      <Dialog open={showExtSearch} onOpenChange={setShowExtSearch}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-[#0F172A] flex items-center gap-2"><Users size={20} weight="duotone" className="text-[#1E3A8A]" /> Apollo Candidate Search</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="Search by role or skills..." value={extQuery} onChange={(e) => setExtQuery(e.target.value)} className="h-10 border-[#E5E7EB]" data-testid="ext-candidate-query" />
            <Button onClick={handleExtSearch} className="bg-[#1E3A8A]" data-testid="ext-candidate-search-submit">Search</Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2">
            {extResults.map((c, i) => (
              <div key={i} className="p-3 border border-[#E5E7EB] rounded-lg hover:border-[#C5A059] transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm text-[#0F172A]">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-[#6B7280]">{c.current_role} | {c.location}</p>
                    <div className="flex gap-1 mt-1">{(c.skills || []).map((s, j) => <Badge key={j} variant="secondary" className="text-[10px]">{s}</Badge>)}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => importCandidate(c)} className="text-xs border-[#C5A059] text-[#C5A059]" data-testid={`import-candidate-${i}`}>Import</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 p-2.5 bg-[#F9FAFB] rounded-lg">
      <div className="w-8 h-8 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
        <Icon size={16} weight="duotone" className="text-[#6B7280]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm text-[#0F172A] font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
